import type { SubmitContactUsRequestInput } from '@mdvip/shared/graphql/generated/graphql';
import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contactUsCategoriesKey, requestSubmitContactUsRequest, SubmitContactUsRequestError } from './contactUsApi';

/**
 * Submits the Contact Us form.
 *
 * Nothing is invalidated on success — the mutation creates no cacheable entity, it just sends mail.
 *
 * `INVALID_CATEGORY` is the one code this hook does not toast: it means the member's dropdown is
 * showing a category the server no longer accepts, so the categories query is refetched and the
 * modal renders the message inline under that field instead. Every other code (including
 * `SEND_FAILED`) toasts. Either way the promise rejects, so the modal stays on the form with the
 * member's input intact.
 */
export function useSubmitContactUsRequest() {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: SubmitContactUsRequestInput) => requestSubmitContactUsRequest(input),
    onError: (error: unknown) => {
      const code = error instanceof SubmitContactUsRequestError ? error.code : extractGqlErrorCode(error);
      // `trackException` forwards `.message` to Application Insights verbatim, and a raw `ClientError`
      // embeds this mutation's own PHI-bearing input via graphql-request's `JSON.stringify`, so only
      // a sanitized error ever reaches telemetry.
      trackException(new Error(`submitContactUsRequest failed: ${code}`), {
        source: 'submitContactUsRequest',
        context: 'contactUs',
        errorCode: code,
      });
      if (code === 'INVALID_CATEGORY') {
        void queryClient.invalidateQueries({ queryKey: contactUsCategoriesKey });
        return;
      }
      toast({
        title: tError('title'),
        description: tError(code, { phone: MDVIP_COMPANY_INFO.memberSupportPhone }),
        variant: 'destructive',
      });
    },
  });

  return {
    submit: (input: SubmitContactUsRequestInput) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}
